<template>
  <div :class="containerClass">
    <page-header
      :title="this.$route.params.id ? 'Edit Class' : 'Add New Class'"
      :container-class="null"
      class="mb-16pt"
    />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label-for="program" label-class="form-label">
        <label for="program" class="form-label">Program <span class="text-danger">*</span></label>
        <v-select
          id="program"
          class="form-control v-select-custom"
          :class="!$v.program.required && $v.program.$dirty ? 'form-control is-invalid' : ''"
          label="title"
          v-model="program"
          :reduce="program => program.id"
          placeholder="Select Program"
          :options="allPrograms"
          :loading="areProgramsLoading"
          @input="programChanged"
        >
          <template #search="{ attributes, events }">
            <input class="vs__search" v-bind="attributes" v-on="events" />
          </template>

          <template slot="option" slot-scope="option">
            <div class="d-flex align-items-center justify-content-between w-100">
              <div class="d-flex align-items-center">
                <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                  <b-img :src="option.image" class="img-fluid" width="20" alt="Logo" v-if="option.image" />
                  <md-icon v-else>local_library</md-icon>
                </fmv-avatar>
                <span>{{ option.title }}</span>
              </div>
              <b-badge 
                v-if="option.type" 
                :variant="option.type === 'virtual' ? 'info' : option.type === 'blended' ? 'warning' : 'success'"
                class="text-capitalize ml-2"
                style="font-size: 0.7rem; padding: 0.2rem 0.3rem;"
              >
                {{ option.type === 'blended' ? 'hybrid' : option.type === 'onsite' ? 'in-person' : option.type === 'virtual' ? 'online' : option.type }}
              </b-badge>
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.title }}
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.program.required && $v.program.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group v-if="[PROGRAM_TYPE.ONSITE, PROGRAM_TYPE.BLENDED].includes(programType)">
        <label for="location" class="form-label"
          >Location
          <a href="#" @click.prevent="modals.showPostLocationModal = true"
            >+Add New? <span class="text-danger">*</span></a
          ></label
        >
        <v-select
          label="id"
          :options="allLocations"
          class="form-control v-select-custom"
          :class="!$v.location.required && $v.location.$dirty ? 'form-control is-invalid' : ''"
          v-model="location"
          :reduce="lc => lc.id"
          placeholder="Enter Location"
          :loading="areLocationsLoading"
          :filter-by="filterLocations"
        >
          <template slot="option" slot-scope="option">
            {{ get(option, 'address') }}, {{ get(option, 'city') }},

            {{ get(option, 'state') }}
            {{ get(option, 'zipcode') }},
            {{ get(option, 'country') }}
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ get(option, 'address') }}, {{ get(option, 'city') }},

            {{ get(option, 'state') }}
            {{ get(option, 'zipcode') }},
            {{ get(option, 'country') }}
          </template>
        </v-select>

        <b-form-invalid-feedback :state="!$v.location.required && $v.location.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group
        label="Class URL"
        label-for="class_link"
        label-class="form-label"
        v-if="[PROGRAM_TYPE.VIRTUAL, PROGRAM_TYPE.BLENDED].includes(programType)"
      >
        <b-form-input
          id="class_link"
          placeholder="Enter Class Link"
          v-model="class_link"
          :state="!$v.class_link.url && $v.class_link.$dirty ? false : null"
        />
        <b-form-invalid-feedback v-if="!$v.class_link.url">
          Not a valid link. Please make sure to enter a valid link e.g. https://www.example.com.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label-class="form-label">
        <b-form-checkbox v-model="limited_seats" name="check-button" switch>Limit Seats</b-form-checkbox>
      </b-form-group>

      <b-form-group label-for="available_seats" label-class="form-label" v-if="limited_seats">
        <label for="available_seats" class="form-label">Available Seats <span class="text-danger">*</span></label>
        <b-form-input
          id="available_seats"
          v-model="available_seats"
          type="number"
          placeholder="Enter Number of Available Seats"
          :state="!$v.available_seats.required && $v.available_seats.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label-for="Instructors" label-class="form-label">
        <label for="Instructors" class="form-label">Instructors <span class="text-danger">*</span></label>
        <v-select
          id="type"
          class="form-control v-select-custom"
          label="first_name"
          v-model="instructors"
          :reduce="ins => ins.id"
          placeholder="Select instructors"
          :options="allInstructors"
          :loading="areInstructorsLoading"
          :class="!$v.instructors.required && $v.instructors.$dirty ? 'form-control is-invalid' : ''"
          multiple
        >
          <template slot="option" slot-scope="option"> {{ option.first_name }} {{ option.last_name }} </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.first_name }} {{ option.last_name }}
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.instructors.required && $v.instructors.$dirty ? false : null"
          >This field is required.</b-form-invalid-feedback
        >
      </b-form-group>

      <b-form-group label-for="class-self-paced" label-class="form-label" v-slot="{ ariaDescribedby }">
        <label for="class-self-paced" class="form-label">Type <span class="text-danger">*</span></label>
        <b-form-radio-group
          id="class-self-paced"
          v-model="classSelfPaced"
          :options="typeOptions"
          :aria-describedby="ariaDescribedby"
        ></b-form-radio-group>
      </b-form-group>

      <div class="row" v-if="classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED">
        <b-form-group label-for="start_date" label-class="form-label" class="col-md-6">
          <label for="start_date" class="form-label">Start Date <span class="text-danger">*</span></label>
          <date-picker
            v-model="start_date"
            format="DD MMMM, YYYY"
            valueType="YYYY-MM-DD"
            style="width: 100%"
            id="start_date"
            lang="en"
            placeholder="Enter Start Date"
            :state="!$v.start_date.required && $v.start_date.$dirty ? false : null"
            :input-class="
              `mx-input ${!$v.start_date.required && $v.start_date.$dirty ? 'form-control is-invalid' : ''}`
            "
            :disabled="isFormLoading"
            @input="setStartDate"
          ></date-picker>
          <b-form-invalid-feedback :state="!$v.start_date.required && $v.start_date.$dirty ? false : null">
            This field is required.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label-for="end_date" label-class="form-label" class="col-md-6">
          <label for="end_date" class="form-label">End Date <span class="text-danger">*</span></label>
          <date-picker
            v-model="end_date"
            format="DD MMMM, YYYY"
            valueType="YYYY-MM-DD"
            style="width: 100%"
            id="end_date"
            lang="en"
            placeholder="Enter End Date"
            :state="!$v.end_date.required && $v.end_date.$dirty ? false : null"
            :input-class="
              `mx-input ${
                (!$v.end_date.required || !$v.end_date.afterStartDate) && $v.end_date.$dirty
                  ? 'form-control is-invalid'
                  : ''
              }`
            "
            :disabled="isFormLoading"
          ></date-picker>
          <b-form-invalid-feedback :state="!$v.end_date.required && $v.end_date.$dirty ? false : null">
            This field is required.
          </b-form-invalid-feedback>
          <b-form-invalid-feedback :state="!$v.end_date.afterStartDate && $v.end_date.$dirty ? false : null">
            End date must be after start date.
          </b-form-invalid-feedback>
        </b-form-group>
      </div>

      <b-form-group label="Total Hours" label-for="total_hours" label-class="form-label">
        <currency-input
          id="amount"
          v-model="total_hours"
          class="form-control"
          :placeholder="
            classSelfPaced ? `Enter total hours` : `Enter total hours (leave empty to calculate automatically)`
          "
          :allow-negative="false"
          :precision="1"
          :currency="null"
        />
      </b-form-group>
      <page-separator>
        <h4 class="font-weight-bold text-uppercase mb-3" style="font-size: 1.0rem;">ORIENTATION</h4>
      </page-separator>
      <div class="mb-4">
        <b-form-group label="Mode" label-for="orientation_type" label-class="form-label">
          <v-select
            id="orientation_type"
            class="form-control v-select-custom"
            label="text"
            v-model="orientation_type"
            :reduce="item => item.value"
            placeholder="Select Mode"
            :options="orientationTypeOptions"
          >
          </v-select>
        </b-form-group>

        <div class="row">
          <b-form-group label="Orientation Date" label-for="orientation_date" label-class="form-label" class="col-md-6">
            <date-picker
              v-model="orientation_date"
              format="DD MMMM, YYYY"
              valueType="YYYY-MM-DD"
              style="width: 100%"
              id="orientation_date"
              lang="en"
              placeholder="MM/DD/YYYY"
            ></date-picker>
          </b-form-group>
          <b-form-group label="Orientation Time" label-for="orientation_time" label-class="form-label" class="col-md-6">
            <date-picker
              v-model="orientation_time"
              lang="en"
              type="time"
              value-type="HH:mm"
              format="hh:mm a"
              :minute-step="15"
              style="width: 100%"
              :placeholder="'Select Orientation Time'"
            ></date-picker>
          </b-form-group>
        </div>
        <b-form-group
          label-for="orientation_link"
          label-class="form-label"
          v-if="orientation_type == ORIENTATION_TYPE.ONLINE"
        >
          <label for="orientation_link" class="form-label">Orientation Link <span class="text-danger">*</span></label>
          <b-form-input
            id="orientation_link"
            v-model="orientation_link"
            type="text"
            placeholder="Enter Orientation Link"
            :state="!$v.orientation_link.required && $v.orientation_link.$dirty ? false : null"
          />
          <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
        </b-form-group>
        <b-form-group v-if="orientation_type == ORIENTATION_TYPE.IN_PERSON">
          <label for="orientation_loc" class="form-label"
            >Orientation Location <span class="text-danger">*</span></label
          >
          <v-select
            label="id"
            :options="allLocations"
            class="form-control v-select-custom"
            :class="!$v.orientation_loc.required && $v.orientation_loc.$dirty ? 'form-control is-invalid' : ''"
            v-model="orientation_loc"
            :reduce="lc => lc.id"
            placeholder="Enter Orientation Location"
            :loading="areLocationsLoading"
            :filter-by="filterLocations"
          >
            <template slot="option" slot-scope="option">
              {{ get(option, 'address') }}, {{ get(option, 'city') }},

              {{ get(option, 'state') }}
              {{ get(option, 'zipcode') }},
              {{ get(option, 'country') }}
            </template>
            <template slot="selected-option" slot-scope="option">
              {{ get(option, 'address') }}, {{ get(option, 'city') }},

              {{ get(option, 'state') }}
              {{ get(option, 'zipcode') }},
              {{ get(option, 'country') }}
            </template>
          </v-select>

          <b-form-invalid-feedback :state="!$v.orientation_loc.required && $v.orientation_loc.$dirty ? false : null"
            >This field is required.</b-form-invalid-feedback
          >
        </b-form-group>
      </div>
      <page-separator v-if="classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED">
        <h4 class="font-weight-bold text-uppercase mb-3" style="font-size: 1rem;">SCHEDULE</h4>
      </page-separator>

      <div
        v-if="classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED"
        class="mb-4"
      >
        <b-form-group label-for="schedule_type" label-class="form-label">
          <label for="schedule_type" class="form-label">Type <span class="text-danger">*</span></label>
          <v-select
            id="schedule_type"
            class="form-control v-select-custom"
            label="text"
            v-model="schedule_type"
            :reduce="item => item.value"
            placeholder="Select Schedule Type"
            :options="scheduleTypeOptions"
            :class="!$v.schedule_type.required && $v.schedule_type.$dirty ? 'form-control is-invalid' : ''"
          >
          </v-select>
          <b-form-invalid-feedback :state="!$v.schedule_type.required && $v.schedule_type.$dirty ? false : null"
            >This field is required.</b-form-invalid-feedback
          >
        </b-form-group>
        <div class="row align-items-center">
          <div class="col-lg-auto mb-2">
            <b-button
              size="sm"
              class="mr-2"
              @click="selectAllRows"
              v-if="classSchedules.length > 0 && selected.length === 0"
              >Select all</b-button
            >
            <b-button
              size="sm"
              class="mr-2"
              @click="clearSelected"
              v-if="classSchedules.length > 0 && selected.length > 0"
              >Clear</b-button
            >
            <b-button size="sm" variant="danger" v-if="selected.length > 0" @click="deleteSelected"
              >Delete Selected</b-button
            >
          </div>
          <div class="col-lg d-flex flex-wrap justify-content-end mb-2">
            <b-btn
              :class="{ 'mb-2': isMobileScreen }"
              variant="primary"
              :disabled="!(program && start_date && end_date && isScheduleEmpty && programStartTime && programEndTime)"
              @click.prevent="openAutoGenModal"
              size="sm"
            >
              <span>Auto-Generate Schedule</span>
            </b-btn>
            <b-btn variant="primary" :disabled="isLoading" @click.prevent="openScheduleModal()" size="sm" class="ml-2">
              <span>Add Custom Schedule</span>
            </b-btn>
          </div>
        </div>
        <div class="mb-2">
          <b-table
            v-if="classSchedules.length > 0"
            :fields="scheduleTableColumns"
            :items="classSchedules"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap mt-2"
            hover
            responsive
            no-local-sorting
            select-mode="multi"
            ref="selectableTable"
            selectable
            @row-selected="onRowSelected"
          >
            <template #cell(selected)="{ rowSelected }">
              <template v-if="rowSelected">
                <md-icon>check</md-icon>
              </template>
            </template>

            <template #cell(menu)="data">
              <div class="text-center">
                <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted" no-caret>
                  <template #button-content>
                    <i class="material-icons icon-24pt card-course__icon-favorite text-info">more_vert</i>
                    <span class="sr-only"></span>
                  </template>

                  <b-dropdown-item
                    @click.prevent="openScheduleModal(data.item, data.index)"
                  >
                    <md-icon class="text-info">edit</md-icon> Edit Schedule
                  </b-dropdown-item>

                  <b-dropdown-item
                    @click.prevent="removeExpEduItem(data.index)"
                  >
                    <md-icon class="text-danger">delete</md-icon> Remove Schedule
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </template>

            <template #cell(start_time)="data">
              <span v-if="data.item.is_self_paced">
                <i>Self-paced</i>
              </span>
              <span v-else
                >{{ formatTime(data.item.start_time) }}
                {{ formatTimeZone(getLoggedInUser.linked_entity.time_zone) }}</span
              >
            </template>
            <template #cell(end_time)="data">
              <span v-if="data.item.is_self_paced">
                <i>Self-paced</i>
              </span>
              <span v-else
                >{{ formatTime(data.item.end_time) }}
                {{ formatTimeZone(getLoggedInUser.linked_entity.time_zone) }}</span
              >
            </template>
          </b-table>
          <b-form-invalid-feedback
            v-if="(!$v.classSchedules.required || !$v.classSchedules.minLength) && $v.classSchedules.$dirty"
            :state="
              (!$v.classSchedules.required || !$v.classSchedules.minLength) && $v.classSchedules.$dirty ? false : null
            "
            >There isn't any schedule added. Please add class schedule.</b-form-invalid-feedback
          >
          <i v-else-if="!classSchedules.length">There isn't any schedule added.</i>
        </div>
      </div>

      <!-- Payment Plan -->

      <page-separator v-if="classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED">
        <h4 class="font-weight-bold text-uppercase mb-3" style="font-size: 1rem;">PAYMENT PLANS</h4>
      </page-separator>

      <div class="mb-4">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <span class="text-muted">Students choose one of these plans when they register.</span>
          <b-btn variant="primary" :disabled="isLoading" @click.prevent="addPaymentPlan()">
            + Add Payment Plan
          </b-btn>
        </div>

        <div v-for="(plan, planIndex) in paymentPlans" :key="planIndex" class="plan-card mb-3">
          <!-- Plan header -->
          <div class="plan-card__header">
            <span class="plan-card__number bg-primary text-white">{{ planIndex + 1 }}</span>

            <div class="plan-card__title-wrap">
              <b-form-input
                v-model="plan.plan_title"
                class="plan-card__title"
                :placeholder="`Plan ${planIndex + 1} name`"
              />
              <md-icon class="plan-card__title-icon">edit</md-icon>
            </div>

            <b-btn class="plan-card__delete" @click.prevent="removePaymentPlan(planIndex)" title="Remove this plan">
              <md-icon class="text-danger">delete</md-icon>
            </b-btn>
          </div>

          <!-- Installments -->
          <div class="plan-card__body">
            <ol v-if="plan.installments.length" class="installments">
              <li
                v-for="(installment, idx) in plan.installments"
                :key="idx"
                class="installment"
                @click.prevent="openPaymentModal(planIndex, installment, idx)"
              >
                <span class="installment__marker">{{ idx + 1 }}</span>

                <span class="installment__when">
                  <span class="installment__primary">{{ installmentPrimary(installment) }}</span>
                  <span class="installment__secondary">{{ installmentSecondary(installment) }}</span>
                </span>

                <span class="installment__money">
                  <span class="installment__amount">{{
                    $n(installmentAmount(installment), 'currency', 'en-US')
                  }}</span>
                  <span v-if="showInstallmentShare" class="installment__share">{{
                    installmentShare(installment)
                  }}</span>
                </span>

                <span class="installment__menu" @click.stop>
                  <b-dropdown :boundary="'main'" no-flip variant="flush" toggle-class="text-muted p-0" no-caret right>
                    <template #button-content>
                      <md-icon class="installment__menu-icon">more_vert</md-icon>
                      <span class="sr-only">Menu</span>
                    </template>

                    <b-dropdown-item @click.prevent="openPaymentModal(planIndex, installment, idx)">
                      <md-icon class="text-info">edit</md-icon> Edit Installment
                    </b-dropdown-item>

                    <b-dropdown-item @click.prevent="removePaymentItem(planIndex, idx)">
                      <md-icon class="text-danger">delete</md-icon> Remove Installment
                    </b-dropdown-item>
                  </b-dropdown>
                </span>
              </li>
            </ol>

            <p v-else class="installments-empty">No installments in this plan yet.</p>

            <b-btn class="plan-card__add-payment" @click.prevent="openPaymentModal(planIndex)">
              + Add Installment
            </b-btn>
          </div>

          <!-- Plan total -->
          <div class="plan-card__footer">
            <span class="plan-card__count">{{ installmentCountLabel(plan) }}</span>
            <span class="plan-card__total">Plan total <b>{{ $n(planTotal(plan), 'currency', 'en-US') }}</b></span>
          </div>

          <div v-if="planErrorMessage(plan)" class="plan-card__error">
            {{ planErrorMessage(plan) }}
          </div>
        </div>

        <div class="plan-empty">
          {{ paymentPlans.length ? 'No additional plans yet' : "You haven't added a payment plan yet" }}
        </div>
      </div>

      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : $t('add') }}</span>
      </b-btn>
    </b-form>
    <class-sch-modal
      :show-modal="modals.showClassSchModal"
      :class-sch="modals.selectedClassSch"
      :class-sch-index="modals.selectedClassSchIndex"
      :class-sch-arr="modals.classSchArray"
      :start-date="modals.startDate"
      :end-date="modals.endDate"
      @close="hideClassSchModal"
      @closeUpdate="hideUpdateClassSchModal"
    />
    <payment-plan-modal
      :show-modal="modals.showClassPayModal"
      :type="paymentPlanAmountType"
      :payment="modals.selectedPayment"
      :payment-index="modals.selectedPaymentIndex"
      :isClassSelfPaced="classSelfPaced"
      :program-tuition="get(programSelected, 'tuition_fee', 0)"
      @close="hidePaymentModal"
      @closeUpdate="hideUpdatePaymentModal"
    />
    <post-location-modal
      :show-modal.sync="modals.showPostLocationModal"
      v-if="modals.showPostLocationModal"
      @close="hidePostLocModal"
      @closeUpdate="updateLocation"
    />
    <auto-gen-modal
      :show-modal.sync="showOpenGenModal"
      :self-paced-days.sync="selfPacedDays"
      :type="programType"
      @setSelfPace="setSelfPaceDays"
      @close="hideAutoGenModal"
      @closeUpdate="updateDaysToExclude"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
// import { BFormTimepicker } from 'bootstrap-vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { FmvAvatar } from 'fmv-avatar';
import { get, orderBy } from 'lodash';
import Page from '../../../components/Page.vue';

import {
  USER_ROLES,
  PROGRAM_TYPE,
  DEFAULT_PAGE_SIZE,
  SCHEDULE_TYPE_OPTIONS,
  PAYMENT_TYPE,
  CLASS_SCHEDULE_TYPES,
  ORIENTATION_TYPE,
  activityCategoryLabel,
} from '../../../common/constants';
import {
  formatToAPIDate,
  formatFullDate,
  formatTime,
  formatTimeZone,
  formatDayDate,
  formatDateFullYear,
} from '../../../common/utils';
import { required, requiredIf, url, minLength } from 'vuelidate/lib/validators';
import DatePicker from 'vue2-datepicker';

import moment from 'moment-timezone';

import 'vue-swatches/dist/vue-swatches.css';
import ClassSchModal from './ClassSchModal.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import PostLocationModal from './PostLocationModal.vue';
import PaymentPlanModal from './PaymentPlanModal.vue';
import AutoGenModal from './AutoGenModal.vue';

import vSelect from 'vue-select';
export default {
  components: {
    PageHeader,
    PageSeparator,
    FmvAvatar,
    MdIcon,
    DatePicker,
    ClassSchModal,
    PostLocationModal,
    PaymentPlanModal,
    AutoGenModal,
    vSelect,
    // BFormTimepicker,
  },
  extends: Page,
  name: 'PostClass',
  data() {
    return {
      title: this.$route.params.id ? 'Edit Class' : 'Add Class',
      tinemceApiKey: process.env.VUE_APP_TINEMCE_API_KEY,
      PAYMENT_TYPE,
      start_date: '',
      end_date: '',
      durationType: '',
      program: null,
      areProgramsLoading: false,
      allSchools: [],
      isLoading: false,
      instructors: [],
      allPrograms: [],
      USER_ROLES,
      PROGRAM_TYPE,
      location: null,
      class_link: '',
      instructor_name: '',
      available_seats: null,
      programStartTime: '',
      programEndTime: '',
      duration: '',
      programType: '',
      classSchedules: [],
      paymentPlans: [],
      limited_seats: true,
      orientation_date: null,
      orientation_time: null,
      schedule_type: '',
      scheduleTypeOptions: SCHEDULE_TYPE_OPTIONS,
      programSelected: {},
      paymentPlanAmountType: null,
      selected: [],
      showOpenGenModal: false,
      selfPacedDays: 0,
      total_hours: null,
      selfPaceStartDate: '',
      selfPaceEndDate: '',
      modals: {
        showPostLocationModal: false,
        showClassSchModal: false,
        selectedClassSch: null,
        selectedClassSchIndex: null,
        startDate: '',
        endDate: '',
        classSchArray: [],

        showClassPayModal: false,
        selectedPayment: null,
        selectedPaymentIndex: null,
        selectedPlanIndex: null,
        paymentStartDate: '',
        paymentEndDate: '',
        paymentArray: [],
      },
      classSelfPaced: CLASS_SCHEDULE_TYPES.SCHEDULED,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalSchedules: 0,
      areInstructorsLoading: false,
      allInstructors: [],
      allLocations: [],
      areLocationsLoading: false,
      daysToExclude: [],
      windowWidth: window.innerWidth,
      typeOptions: [
        { value: 'scheduled', text: 'Scheduled' },
        { value: 'self_paced', text: 'Self-Paced' },
      ],
      orientation_type: '',
      orientation_link: '',
      orientation_loc: '',
      orientationTypeOptions: [
        { value: 'in_person', text: 'In-Person' },
        { value: 'online', text: 'Online' },
      ],
      ORIENTATION_TYPE,
      CLASS_SCHEDULE_TYPES,
    };
  },

  validations() {
    return {
      program: { required },
      start_date: { required: requiredIf(() => this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED) },

      schedule_type: { required: requiredIf(() => this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED) },
      orientation_link: { required: requiredIf(() => this.orientation_type === ORIENTATION_TYPE.ONLINE) },
      orientation_loc: { required: requiredIf(() => this.orientation_type === ORIENTATION_TYPE.IN_PERSON) },
      end_date: {
        required: requiredIf(() => this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED),
        afterStartDate: value =>
          !this.start_date ||
          !value ||
          moment(value, 'YYYY-MM-DD').isAfter(moment(this.start_date, 'YYYY-MM-DD')) ||
          moment(value, 'YYYY-MM-DD').isSame(moment(this.start_date, 'YYYY-MM-DD')),
      },
      instructors: { required },

      location: { required: requiredIf(() => [PROGRAM_TYPE.ONSITE, PROGRAM_TYPE.BLENDED].includes(this.programType)) },
      limited_seats: { required },
      available_seats: { required: requiredIf(() => this.limited_seats) },
      class_link: { url },
      classSchedules: {
        required: requiredIf(() => this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED),
        minLength: minLength(1),
      },
      paymentPlans: {
        // Every plan a class offers must independently cover the full tuition -- the
        // student pays exactly one of them.
        allPlansValid: () => this.paymentPlans.every(plan => !this.planErrorMessage(plan)),
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        {
          text: 'Classes',
          to: {
            name: 'classes-management-list',
          },
        },
        {
          text: this.$route.params.id ? 'Edit Class' : 'Add Class',
          active: true,
        },
      ];
    },
    isScheduleEmpty: ({ classSchedules }) => classSchedules.length === 0,

    // The share of tuition is an authoring aid for percentage plans; dollar plans
    // already read as the amount they are.
    showInstallmentShare() {
      return this.paymentPlanAmountType === PAYMENT_TYPE.PERCENTAGE;
    },
    isFormLoading() {
      return this.isLoading || this.areProgramsLoading;
    },

    isMobileScreen() {
      return this.windowWidth <= 420;
    },

    scheduleTableColumns() {
      return [
        {
          key: 'menu',
          label: '',
          tdClass: 'text-left',
        },
        { key: 'selected', label: '', thStyle: { width: '50px', maxWidth: '50px' } },
        { key: 'date', label: 'date', formatter: value => (value ? formatDayDate(value) : value) },
        {
          key: 'start_time',
          label: 'Start Time',
        },
        {
          key: 'end_time',
          label: 'End Time',
        },
        {
          key: 'activity_category',
          label: 'Activity Category',
          formatter: value => (value ? activityCategoryLabel(value) : '—'),
        },
      ];
    },
  },

  methods: {
    ...mapActions('instructor', ['getAllInstructors']),
    ...mapActions('location', ['getAllLocations']),
    ...mapActions('program', ['getAllPrograms']),
    ...mapActions('classes', ['getClass', 'createClass', 'updateClass']),
    formatTime,
    formatDayDate,
    get,
    formatTimeZone,
    formatFullDate,
    formatDateFullYear,
    setSelfPaceDays(val) {
      this.selfPacedDays = val;
    },

    async deleteSelected() {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to delete the selected schedules.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          const selectedArr = this.classSchedules.filter(x => !this.selected.filter(y => y.id === x.id).length);
          this.classSchedules = selectedArr;
          this.classSchArray = selectedArr;
          this.selected = [];
          this.daysToExclude = [];
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    onRowSelected(items) {
      this.selected = items;
    },
    selectAllRows() {
      this.$refs.selectableTable.selectAllRows();
    },
    clearSelected() {
      this.$refs.selectableTable.clearSelected();
    },

    updateLocation(lc) {
      this.modals.showPostLocationModal = false;
      this.allLocations.unshift(lc);
      this.location = lc.id;
    },
    hidePostLocModal() {
      this.modals.showPostLocationModal = false;
    },

    addPaymentPlan() {
      this.paymentPlans.push({ plan_title: '', installments: [] });
    },

    async removePaymentPlan(planIndex) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm('Please confirm that you want to remove this plan.', {
          title: 'Are you sure?',
          size: 'md',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Yes',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true,
        });
        if (isConfirmed) {
          this.paymentPlans.splice(planIndex, 1);

          if (!this.paymentPlans.some(plan => plan.installments.length)) {
            this.paymentPlanAmountType = null;
          }
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    // Scheduled installments fall on a real date; self-paced ones on an offset from
    // the day the student registers.
    installmentPrimary(installment) {
      if (this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED) {
        return installment.date ? moment(installment.date).format('MMM D, YYYY') : 'No date set';
      }

      const days = Number(installment.days_after) || 0;
      return days ? `Day ${days}` : 'At signup';
    },

    installmentSecondary(installment) {
      if (this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED) {
        return installment.date ? moment(installment.date).format('dddd') : '';
      }

      const days = Number(installment.days_after) || 0;
      return days ? `${days} ${days === 1 ? 'day' : 'days'} after registration` : 'Due on registration';
    },

    // Only percentage plans are entered as a share, so only they show one back.
    installmentShare(installment) {
      const percent = Number(installment.amount) || 0;

      return `${this.$n(percent, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}% of tuition`;
    },

    installmentCountLabel(plan) {
      const count = plan.installments.length;
      return `${count} installment${count === 1 ? '' : 's'}`;
    },

    // Installments are stored either as a percentage of tuition or as a dollar amount.
    installmentAmount(installment) {
      return this.paymentPlanAmountType === PAYMENT_TYPE.PERCENTAGE
        ? this.computePlanAmount(installment.amount)
        : Number(installment.amount) || 0;
    },

    planTotal(plan) {
      return plan.installments.reduce((total, installment) => total + this.installmentAmount(installment), 0);
    },

    // Returns the reason a plan is not payable, or '' when it is valid.
    planErrorMessage(plan) {
      if (!plan.installments.length) {
        return '';
      }

      const total = plan.installments.reduce((sum, obj) => Number(obj.amount) + sum, 0);

      if (this.paymentPlanAmountType === PAYMENT_TYPE.PERCENTAGE) {
        return total === 100
          ? ''
          : 'Payment percentage sum is not equal to 100. Please make sure that the total tuition percentage is 100%.';
      }

      const tuitionFee = get(this.programSelected, 'tuition_fee', 0);
      return total === tuitionFee
        ? ''
        : `Payment sum is not equal to tuition fee. Please make sure that the sum must be equal to total tuition fee ${this.$n(
            tuitionFee,
            'currency',
            'en-US'
          )}.`;
    },

    computePlanAmount(percent) {
      return (percent / 100) * get(this.programSelected, 'tuition_fee', 0);
    },

    async fetchLocation() {
      this.areLocationsLoading = true;

      const response = await this.getAllLocations({});
      this.allLocations = response.data;
      this.areLocationsLoading = false;
    },

    filterLocations(option, label, search) {
      let searchLower = search.toLowerCase();

      return (
        get(option, 'address', '')
          .toLowerCase()
          .indexOf(searchLower) > -1 ||
        get(option, 'city', '')
          .toLowerCase()
          .indexOf(searchLower) > -1 ||
        get(option, 'state', '')
          .toLowerCase()
          .indexOf(searchLower) > -1 ||
        get(option, 'zipcode', '')
          .toLowerCase()
          .indexOf(searchLower) > -1 ||
        get(option, 'country', '')
          .toLowerCase()
          .indexOf(searchLower) > -1
      );
    },

    async fetchInstructor() {
      this.areInstructorsLoading = true;

      const response = await this.getAllInstructors({});
      this.allInstructors = response.data;
      this.areInstructorsLoading = false;
    },
    openAutoGenModal() {
      this.showOpenGenModal = true;
    },
    hideAutoGenModal() {
      this.showOpenGenModal = false;
    },
    updateDaysToExclude(data) {
      this.showOpenGenModal = false;
      this.daysToExclude = data.day;
      this.selfPacedDays = data.self;
      this.populateSchedules();
    },

    populateSchedules() {
      let currentDate = moment(this.start_date);
      const endDate = moment(this.end_date);

      let selfPaceCount = 0;
      while (selfPaceCount < this.selfPacedDays) {
        this.classSchedules.push({
          date: moment(currentDate).format('YYYY-MM-DD'),
          is_self_paced: true,
          activity_category: null,
        });
        selfPaceCount++;
        currentDate = moment(currentDate).add(1, 'days');
      }
      while (currentDate <= endDate) {
        if (!this.daysToExclude.includes(moment(currentDate).day())) {
          this.classSchedules.push({
            date: moment(currentDate).format('YYYY-MM-DD'),
            start_time: this.programStartTime,
            end_time: this.programEndTime,
            is_self_paced: false,
            activity_category: null,
          });
        }
        currentDate = moment(currentDate).add(1, 'days');
      }
    },

    async removeExpEduItem(index) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to delete the selected schedule.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          this.classSchedules.splice(index, 1);
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async removePaymentItem(planIndex, index) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to remove the selected date from the payment plan.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          this.paymentPlans[planIndex].installments.splice(index, 1);

          if (!this.paymentPlans.some(plan => plan.installments.length)) {
            this.paymentPlanAmountType = null;
          }
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    openPaymentModal(planIndex, payment = null, index = null) {
      this.modals.selectedPayment = payment;
      this.modals.selectedPaymentIndex = index;
      this.modals.selectedPlanIndex = planIndex;
      this.modals.showClassPayModal = true;
    },
    hideUpdatePaymentModal(data) {
      const plan = this.paymentPlans[this.modals.selectedPlanIndex];

      this.modals.showClassPayModal = false;
      this.modals.selectedPayment = null;
      this.modals.selectedPaymentIndex = null;

      if (!plan) {
        return;
      }

      if (data.isUpdate) {
        plan.installments.splice(data.index, 1, data.payment);
      } else {
        plan.installments.push(data.payment);
      }
      this.paymentPlanAmountType = data.payment.amount_type;
      plan.installments = orderBy(plan.installments, [i => Number(i.days_after) || 0, 'date'], ['asc', 'asc']);
    },
    hidePaymentModal() {
      // The modal hides itself before emitting closeUpdate, so this runs first on save.
      // selectedPlanIndex must survive it -- openPaymentModal sets it on every open.
      this.modals.showClassPayModal = false;
      this.modals.selectedPayment = null;
      this.modals.selectedPaymentIndex = null;
    },

    openScheduleModal(classSch = null, index = null) {
      this.modals.selectedClassSch = classSch;
      this.modals.selectedClassSchIndex = index;
      if (this.classSchedules.length > 0) {
        this.modals.classSchArray = this.classSchedules.map(sc => sc.date);
      } else {
        this.modals.classSchArray = [];
      }

      this.modals.startDate = this.start_date;
      this.modals.endDate = this.end_date;

      this.modals.showClassSchModal = true;
    },

    hideClassSchModal() {
      this.modals.showClassSchModal = false;
      this.modals.selectedClassSch = null;
      this.modals.selectedClassSchIndex = null;
      this.modals.classSchArray = [];
    },

    hideUpdateClassSchModal(data) {
      this.modals.showClassSchModal = false;
      this.modals.selectedClassSch = null;
      this.modals.selectedClassSchIndex = null;
      this.modals.classSchArray = [];

      if (data.isUpdate) {
        this.classSchedules[data.index] = data.classSch;
      } else {
        this.classSchedules.push(...data.classSch);
      }
      this.classSchedules = orderBy(this.classSchedules, ['date'], ['asc']);
    },

    setStartDate(value) {
      if (this.program) {
        const totalDays = Math.ceil(
          this.duration * (this.durationType === 'months' ? 30.44 : this.durationType === 'weeks' ? 7 : 1)
        );
        const end = moment(value).add(totalDays, 'days');
        this.end_date = formatToAPIDate(end);
      }
    },

    programChanged(value, firstLoad = false) {
      if (value) {
        this.programSelected = this.allPrograms.find(pg => pg.id === value);

        this.programType = this.programSelected.type;
        this.duration = this.programSelected.duration;
        this.durationType = this.programSelected.duration_type;
        this.programStartTime = get(this.programSelected, 'default_class_start_time', '');
        this.programEndTime = get(this.programSelected, 'default_class_end_time', '');
        if (!firstLoad) {
          this.selfPacedDays = this.programSelected.self_paced_days;
          this.total_hours = this.programSelected.default_hours;
          this.classSelfPaced = this.programSelected.schedule_type;
        }

        if (this.start_date && !firstLoad) {
          this.setStartDate(this.start_date);
        }
      } else {
        this.programType = '';
        this.duration = '';
        this.durationType = '';
        this.programStartTime = '';
        this.programEndTime = '';
        this.selfPacedDays = 0;
        this.total_hours = null;
        this.classSelfPaced = CLASS_SCHEDULE_TYPES.SCHEDULED;
      }
    },

    async fetchPrograms() {
      this.areProgramsLoading = true;

      const response = await this.getAllPrograms({});
      this.allPrograms = response.data;

      this.areProgramsLoading = false;
    },

    async fetchClassData() {
      try {
        if (this.$route.params.id) {
          const resp = (await this.getClass(this.$route.params.id)).data;

          this.start_date = resp.start_date;
          this.end_date = resp.end_date;
          // this.instructor_name = resp.instructor_name;
          this.program = get(resp, 'program.id', null);
          this.location = get(resp, 'class_location.id', null);
          this.class_link = get(resp, 'class_link', '');
          this.limited_seats = get(resp, 'limited_seats');
          this.schedule_type = get(resp, 'schedule_type');
          this.orientation_date = resp.orientation_date;
          this.orientation_time = resp.orientation_time;
          this.orientation_type = resp.orientation_type;
          this.orientation_link = resp.orientation_link;
          this.orientation_loc = get(resp, 'orientation_loc.id', null);
          this.available_seats = get(resp, 'available_seats', null);
          this.instructors = resp.instructors.map(ins => ins.id);
          this.selfPacedDays = resp.self_paced_days;
          this.total_hours = resp.total_hours;
          this.modals.startDate = resp.start_date;
          this.modals.endDate = resp.end_date;
          this.classSchedules = orderBy(get(resp, 'schedules', []), ['date'], ['asc']);
          const flatPlanRows = orderBy(get(resp, 'payment_plan', []), ['plan_group', 'date'], ['asc', 'asc']);
          this.paymentPlans = flatPlanRows.reduce((plans, row) => {
            // Rows sharing a plan_group are one selectable plan.
            let plan = plans.find(p => p.plan_group === row.plan_group);
            if (!plan) {
              plan = { plan_group: row.plan_group, plan_title: row.plan_title || '', installments: [] };
              plans.push(plan);
            }
            plan.installments.push(row);
            return plans;
          }, []);
          this.paymentPlanAmountType = get(flatPlanRows, '0.amount_type', null);
          this.totalSchedules = get(resp, 'schedules.length', 0);

          this.classSelfPaced = get(resp, 'class_type');
        }
      } catch (e) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.$router.push({
          name: 'classes-management-list',
        });
      }
    },

    async onSubmit() {
      this.isLoading = true;

      const allInstallments = this.paymentPlans.flatMap(plan => plan.installments);

      if (this.paymentPlans.some(plan => !plan.installments.length)) {
        this.makeToast({ variant: 'danger', msg: 'Every payment plan needs at least one payment.' });
        this.isLoading = false;
        return;
      }

      // Students pick between plans by name, so names are required once there is a choice.
      if (this.paymentPlans.length > 1 && this.paymentPlans.some(plan => !(plan.plan_title || '').trim())) {
        this.makeToast({ variant: 'danger', msg: 'Please name each payment plan.' });
        this.isLoading = false;
        return;
      }

      if (
        allInstallments.length &&
        !allInstallments.every(installment =>
          this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED ? !!installment.date : !!installment.days_after
        )
      ) {
        this.makeToast({
          variant: 'danger',
          msg: `Please fill ${
            this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED ? 'date' : 'days'
          } field correctly.`,
        });
        this.isLoading = false;
        return;
      }

      this.$v.$touch();

      if (!this.$v.$invalid) {
        try {
          const data = {
            start_date:
              this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED
                ? this.start_date
                  ? formatToAPIDate(moment(this.start_date))
                  : null
                : null,
            end_date:
              this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED
                ? this.end_date
                  ? formatToAPIDate(moment(this.end_date))
                  : null
                : null,
            class_location: [PROGRAM_TYPE.ONSITE, PROGRAM_TYPE.BLENDED].includes(this.programType)
              ? this.location
              : null,
            class_link: [PROGRAM_TYPE.VIRTUAL, PROGRAM_TYPE.BLENDED].includes(this.programType)
              ? this.class_link
              : null,
            program: this.program,
            orientation_date: this.orientation_date
              ? formatToAPIDate(moment(this.orientation_date))
              : this.orientation_date,
            orientation_time: this.orientation_time,
            orientation_type: this.orientation_type,
            orientation_link: this.orientation_type === ORIENTATION_TYPE.ONLINE ? this.orientation_link : null,
            orientation_loc: this.orientation_type === ORIENTATION_TYPE.IN_PERSON ? this.orientation_loc : null,
            schedule_type: this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED ? this.schedule_type : null,
            schedules: this.classSelfPaced === CLASS_SCHEDULE_TYPES.SCHEDULED ? this.classSchedules : [],
            payment_plan: this.paymentPlans.flatMap((plan, planIndex) =>
              plan.installments.map(installment => ({
                ...installment,
                plan_group: planIndex + 1,
                plan_title: plan.plan_title,
              }))
            ),
            instructors: this.instructors,
            limited_seats: this.limited_seats,
            available_seats: this.available_seats,
            self_paced_days: this.selfPacedDays || null,
            ...(this.total_hours && { total_hours: this.total_hours }),
            class_type: this.classSelfPaced,
          };

          if (this.$route.params.id) {
            await this.updateClass({
              id: this.$route.params.id,
              data,
            });
            this.makeToast({ variant: 'success', msg: this.$t('Class Updated!') });
          } else {
            await this.createClass({
              ...data,
            });
            this.makeToast({ variant: 'success', msg: 'Class Added!' });
          }

          setTimeout(() => this.$router.push({ name: 'classes-management-list' }), 250);
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }

      this.isLoading = false;
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },

  async mounted() {
    window.addEventListener('resize', this.handleResize);
    this.isLoading = true;

    await Promise.all([this.fetchPrograms(), this.fetchInstructor(), this.fetchClassData(), this.fetchLocation()]);
    this.programChanged(this.program, true);

    this.isLoading = false;
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
.plan-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    background: #fafafa;
    border-bottom: 1px solid #e5e7eb;
  }

  &__number {
    flex: 0 0 auto;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.875rem;
  }

  &__title-wrap {
    position: relative;
    flex: 1 1 auto;
  }

  // Styled as the plan's heading, but always visibly a field you can type in.
  &__title.form-control {
    height: auto;
    padding: 0.4375rem 2.25rem 0.4375rem 0.75rem;
    border: 1px solid #dfe3e8;
    border-radius: 8px;
    background: #fff;
    box-shadow: none;
    font-weight: 700;
    font-size: 1.0625rem;

    &:hover {
      border-color: #c3cad3;
    }

    &::placeholder {
      font-weight: 400;
      color: #a2a9b0;
    }
  }

  &__title-icon {
    position: absolute;
    top: 50%;
    right: 0.625rem;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 1.125rem;
    color: #a2a9b0;
  }

  &__title.form-control:focus ~ &__title-icon {
    color: #6c757d;
  }

  &__delete.btn {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: rgba(220, 53, 69, 0.08);

    &:hover {
      background: rgba(220, 53, 69, 0.16);
    }
  }

  &__body {
    padding: 1rem;
  }

  &__add-payment.btn {
    border: none;
    border-radius: 8px;
    background: #eceef1;
    color: #4a5057;
    font-weight: 700;
    font-size: 0.8125rem;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    padding: 0.625rem 1rem;

    &:hover {
      background: #e0e3e7;
      color: #24272b;
    }
  }

  &__footer {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.875rem 1rem;
    background: #fafafa;
    border-top: 1px solid #e5e7eb;
  }

  &__count {
    color: #6c757d;
    font-size: 0.9375rem;
  }

  &__total {
    color: #6c757d;
    font-size: 0.9375rem;
    white-space: nowrap;

    b {
      color: #131920;
      font-size: 1.0625rem;
    }
  }

  &__error {
    padding: 0 1rem 0.875rem;
    color: #dc3545;
    font-size: 0.8125rem;
  }
}

/* Installments read as a vertical timeline: a numbered stop per payment, joined by a
   connector, so the order and rhythm of the schedule are visible without reading dates. */
.installments {
  list-style: none;
  margin: 0 0 0.75rem;
  padding: 0;
}

.installment {
  position: relative;
  display: grid;
  grid-template-columns: 1.75rem minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0 0.9rem;
  padding: 0.65rem 0.5rem;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f6f8fa;
  }

  /* Connector down to the next stop. */
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 1.3125rem;
    top: 2.4rem;
    bottom: -0.35rem;
    width: 2px;
    background: #e5e7eb;
  }

  &__marker {
    position: relative;
    z-index: 1;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    border: 2px solid #dfe3e8;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8125rem;
    font-weight: 700;
    color: #6c757d;
    font-variant-numeric: tabular-nums;
  }

  &__when {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__primary {
    font-weight: 600;
    color: #131920;
    line-height: 1.3;
  }

  &__secondary {
    font-size: 0.8125rem;
    color: #868e96;
    line-height: 1.3;
  }

  &__money {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
    white-space: nowrap;
  }

  &__amount {
    font-weight: 700;
    color: #131920;
    line-height: 1.3;
    font-variant-numeric: tabular-nums;
  }

  &__share {
    font-size: 0.8125rem;
    color: #868e96;
    line-height: 1.3;
    font-variant-numeric: tabular-nums;
  }

  &__menu {
    display: flex;
    align-items: center;
  }

  &__menu-icon {
    font-size: 1.25rem;
    color: #a2a9b0;
  }

  &:hover &__menu-icon {
    color: #6c757d;
  }
}

.installments-empty {
  margin: 0 0 0.75rem;
  color: #868e96;
  font-style: italic;
}

.plan-empty {
  padding: 1.75rem 1rem;
  border: 1px dashed #d3d8de;
  border-radius: 10px;
  text-align: center;
  color: #868e96;
}
</style>
